
import React, { ReactNode } from 'react';
import VerticalMenu from './VerticalMenu';
import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

interface DashboardLayoutProps {
  children: ReactNode;
}

const DashboardLayout = ({ children }: DashboardLayoutProps) => {
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const { user } = useAuth();
  const isMobile = useIsMobile();
  
  // Toggle theme function
  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    
    // Apply theme to document element for global styling
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
    
    // Store theme preference
    localStorage.setItem('theme', newTheme);
  };
  
  // Load theme from localStorage and apply it
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null;
    
    if (savedTheme) {
      setTheme(savedTheme);
      document.documentElement.classList.toggle('dark', savedTheme === 'dark');
    } else {
      // Check for user's system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setTheme(prefersDark ? 'dark' : 'light');
      document.documentElement.classList.toggle('dark', prefersDark);
    }
  }, []);

  return (
    <div className={cn(
      "min-h-screen bg-gray-50 dark:bg-gray-900 font-[Roboto] transition-colors duration-200",
      isMobile ? "pl-0" : "pl-[100px]", 
      "text-gray-800 dark:text-gray-100"
    )}>
      <VerticalMenu theme={theme} toggleTheme={toggleTheme} />
      <div className={cn(
        "mx-auto py-8 transition-all duration-300",
        isMobile ? "px-4 pt-20" : "py-12 px-6 container"
      )}>
        {children}
      </div>
    </div>
  );
};

export default DashboardLayout;
